import React from 'react';
import PropTypes from 'prop-types';
import {Button, ButtonGroup} from '@/components/atoms';

/**
 * SaveCancelFooter component renders a footer with Cancel, Save Draft, and Next/Publish buttons.
 *
 * @param {Object} props - The component props.
 * @param {Function} props.handleCancel - Function to handle the cancel action.
 * @param {Function} props.draftAction - Function to handle the save draft action.
 * @param {Function} props.nextAction - Function to handle the next or publish action.
 * @param {boolean} [props.isReview=false] - Flag to determine if the current step is review.
 * @param {Array} [props.FormError=[]] - Array of form errors to determine if the next button should be disabled.
 * @param {string} [props.wrapperClassName=''] - Optional CSS class name to apply to the wrapper div.
 *
 * @returns {JSX.Element} The rendered SaveCancelFooter component.
 */
const SaveCancelFooter = ({
  handleCancel,
  draftAction,
  nextAction,
  isReview = false,
  nextButtonTitle,
  hideSaveDraft,
  FormError = false,
  disableDraft = false,
  wrapperClassName = ''
}) => {
  const saveDraftSchema = () => {
    if (hideSaveDraft) {
      return handleCancel ? [{label: 'Cancel', onClick: handleCancel}] : [];
    }
    return [
      {label: 'Cancel', onClick: handleCancel},
      {label: 'Save Draft', onClick: draftAction, disabled: disableDraft}
    ];
  };

  return (
    <div className={`flex justify-between py-4 ${wrapperClassName}`}>
      <ButtonGroup
        uiSchema={{
          items: saveDraftSchema()
        }}
        id='save-cancel-footer'
      />
      <Button
        handleButton={nextAction}
        uiSchema={{
          className: 'ml-4',
          title: nextButtonTitle || (isReview ? 'Publish' : 'Next'),
          variant: 'primary',
          size: 'medium',
          iconTrailing: 'arrow_forward',
          disabled: FormError
        }}
      />
    </div>
  );
};

SaveCancelFooter.propTypes = {
  handleCancel: PropTypes.func.isRequired,
  draftAction: PropTypes.func.isRequired,
  nextAction: PropTypes.func.isRequired,
  isReview: PropTypes.bool,
  nextButtonTitle: PropTypes.string,
  hideSaveDraft: PropTypes.bool,
  FormError: PropTypes.oneOfType([PropTypes.array, PropTypes.bool]),
  disableDraft: PropTypes.bool,
  wrapperClassName: PropTypes.string
};

export default React.memo(SaveCancelFooter);
